// src/components/admin/pages/TermsPage.js
import React from 'react';
import { 
  FileText, Shield, Lock, MessageCircle, CheckCircle2, 
  AlertCircle, Scale, Mail, MapPin, Clock, FileCheck, 
  Globe, Users, Key, Eye
} from 'lucide-react';

export default function TermsPage() {
  const lastUpdate = "10 de septiembre de 2026";

  return (
    <div className="w-full">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-10 lg:p-12">
        {/* Header Sobrio */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4">
            <FileText className="w-8 h-8 text-slate-700" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3 tracking-tight">
            Términos y Condiciones de Uso
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Una guía transparente sobre cómo funcionamos juntos.
          </p>
          <p className="text-sm text-slate-500 mt-4 flex items-center justify-center gap-2">
            <Clock className="w-4 h-4" />
            Última actualización: {lastUpdate}
          </p>
        </div>

        {/* Introducción */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 md:p-8 mb-12">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-white border border-slate-200 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
              <MessageCircle className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">Bienvenido a Inkless</h2>
              <p className="text-slate-600 leading-relaxed">
                En Inkless queremos que te sientas seguro usando nuestra plataforma. 
                Estos términos no son un documento legal intimidante, sino una guía transparente 
                sobre cómo funcionamos. Te los explicamos en lenguaje sencillo, porque creemos 
                que la confianza se construye con claridad.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-12">
          {/* SECCIÓN 1 */}
          <section className="border-b border-slate-200 pb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                <Users className="w-4 h-4 text-blue-600" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">1. ¿Qué estás aceptando?</h2>
            </div>
            <div className="space-y-4 text-slate-600 leading-relaxed pl-11">
              <p>
                Al usar Inkless (disponible en firma.visioner7.pe), estás de acuerdo con estos 
                términos. Es como cuando entras a una cafetería: al pedir un café, aceptas sus reglas 
                básicas. Aquí es similar: al usar nuestro servicio, aceptas estas condiciones que 
                protegen tanto a ti como a nosotros.
              </p>
              <p>
                Si algo cambia en el futuro, te avisaremos con tiempo. Y si no estás de acuerdo con 
                algún cambio, siempre puedes dejar de usar el servicio.
              </p>
            </div>
          </section>

          {/* SECCIÓN 2 */}
          <section className="border-b border-slate-200 pb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                <FileCheck className="w-4 h-4 text-blue-600" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">2. ¿Qué hacemos por ti?</h2>
            </div>
            <p className="text-slate-600 leading-relaxed mb-6 pl-11">
              Somos una plataforma tecnológica que te ayuda a gestionar tus documentos de forma segura:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6 pl-11">
              {[
                { icon: FileCheck, text: 'Subir y gestionar documentos PDF de forma segura' },
                { icon: FileText, text: 'Generar enlaces para firma electrónica' },
                { icon: Shield, text: 'Verificar que un documento no ha sido alterado' },
                { icon: Clock, text: 'Aplicar sellos de tiempo certificados' },
                { icon: Eye, text: 'Mantener un registro completo de auditoría' },
                { icon: Lock, text: 'Proteger tus documentos con encriptación avanzada' }
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-slate-50 border border-slate-200 p-4 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-700 font-medium">{item.text}</span>
                </div>
              ))}
            </div>
            <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-5 pl-11">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700 leading-relaxed">
                  <strong>Importante:</strong> Nosotros proporcionamos la tecnología, pero no somos parte 
                  de los acuerdos legales que tú celebres con tus clientes. Es como cuando usas WhatsApp: 
                  la app te permite comunicarte, pero no es responsable del contenido de tus mensajes.
                </p>
              </div>
            </div>
          </section>

          {/* SECCIÓN 3 */}
          <section className="border-b border-slate-200 pb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                <Shield className="w-4 h-4 text-blue-600" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">3. ¿Qué esperamos de ti?</h2>
            </div>
            <p className="text-slate-600 leading-relaxed mb-6 pl-11">
              Para que todo funcione bien, te pedimos que:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-11">
              {[
                { title: 'Seas mayor de edad', desc: 'Tener 18 años o más (o la edad legal en tu país)', icon: Users },
                { title: 'Uses información real', desc: 'Proporcionar datos verdaderos al registrarte', icon: FileCheck },
                { title: 'Protejas tu cuenta', desc: 'Mantener tu contraseña en secreto y no compartirla', icon: Key },
                { title: 'Subas contenido legal', desc: 'No usar la plataforma para documentos ilegales', icon: FileText },
                { title: 'Respetes a otros', desc: 'No subir contenido ofensivo o que infrinja derechos', icon: Shield },
                { title: 'No hagas trampa', desc: 'No intentar hackear o usar bots automatizados', icon: Lock }
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-slate-50 border border-slate-200 p-4 rounded-lg">
                  <div className="w-8 h-8 bg-white border border-slate-200 rounded-md flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-4 h-4 text-slate-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 text-sm mb-1">{item.title}</h3>
                    <p className="text-xs text-slate-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* SECCIÓN 4 */}
          <section className="border-b border-slate-200 pb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                <Lock className="w-4 h-4 text-blue-600" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">4. Sobre tus documentos</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pl-11">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
                <div className="w-10 h-10 bg-white border border-slate-200 rounded-lg flex items-center justify-center mb-3">
                  <FileText className="w-5 h-5 text-slate-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">¿Quién es el dueño?</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  <strong>Tú. Siempre.</strong> Los documentos que subes son 100% tuyos. Nosotros solo los 
                  almacenamos temporalmente para que puedas usar nuestro servicio. No los vendemos ni 
                  los compartimos (excepto cuando la ley lo exige).
                </p>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
                <div className="w-10 h-10 bg-white border border-slate-200 rounded-lg flex items-center justify-center mb-3">
                  <Clock className="w-5 h-5 text-slate-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">¿Cuánto tiempo los guardamos?</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Por defecto, <strong>72 horas</strong> desde que los creas. Después de eso, se eliminan 
                  automáticamente de forma segura. Si necesitas más tiempo, puedes contratar un plan con 
                  almacenamiento prolongado.
                </p>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
                <div className="w-10 h-10 bg-white border border-slate-200 rounded-lg flex items-center justify-center mb-3">
                  <AlertCircle className="w-5 h-5 text-slate-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Nuestro consejo</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Descarga tus documentos firmados lo antes posible. Aunque hacemos copias de seguridad, 
                  recomendamos no depender únicamente de nuestro almacenamiento.
                </p>
              </div>
            </div>
          </section>

          {/* SECCIÓN 5 */}
          <section className="border-b border-slate-200 pb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                <Scale className="w-4 h-4 text-blue-600" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">5. Nuestra responsabilidad</h2>
            </div>
            <p className="text-slate-600 leading-relaxed mb-6 pl-11">
              Queremos ser transparentes sobre nuestros compromisos y límites:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-11">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-blue-600" /> Lo que garantizamos:
                </h3>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li>• Que la plataforma funcionará correctamente la mayor parte del tiempo.</li>
                  <li>• Que tus datos estarán protegidos con encriptación de nivel empresarial.</li>
                  <li>• Que responderemos a tus consultas en un plazo razonable.</li>
                </ul>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-slate-500" /> Lo que no podemos garantizar:
                </h3>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li>• Que el servicio estará disponible el 100% del tiempo (pueden haber mantenimientos).</li>
                  <li>• Que la firma electrónica será aceptada en todos los países para todos los documentos.</li>
                  <li>• Que podremos recuperar documentos eliminados después del período de retención.</li>
                </ul>
              </div>
            </div>
            <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-5 mt-4 pl-11">
              <p className="text-sm text-slate-700 leading-relaxed">
                <strong>Límite de responsabilidad:</strong> En el improbable caso de que algo salga muy mal, 
                nuestra responsabilidad máxima será de <strong>$100 USD</strong> o el monto que hayas pagado 
                en los últimos 6 meses (lo que sea mayor). Esto es estándar en la industria de software y 
                nos permite ofrecer el servicio a un precio accesible.
              </p>
            </div>
          </section>

          {/* SECCIÓN 6 */}
          <section className="border-b border-slate-200 pb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                <Globe className="w-4 h-4 text-blue-600" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">6. Validez legal de las firmas</h2>
            </div>
            <p className="text-slate-600 leading-relaxed mb-6 pl-11">
              Las firmas electrónicas que generamos cumplen con estándares internacionales:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 pl-11">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 text-center">
                <Globe className="w-8 h-8 text-slate-600 mx-auto mb-3" />
                <p className="text-xs font-bold text-slate-900 mb-1">PERÚ</p>
                <p className="text-sm text-slate-600">Ley N° 27269</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 text-center">
                <Globe className="w-8 h-8 text-slate-600 mx-auto mb-3" />
                <p className="text-xs font-bold text-slate-900 mb-1">UNIÓN EUROPEA</p>
                <p className="text-sm text-slate-600">Reglamento eIDAS</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 text-center">
                <Globe className="w-8 h-8 text-slate-600 mx-auto mb-3" />
                <p className="text-xs font-bold text-slate-900 mb-1">INTERNACIONAL</p>
                <p className="text-sm text-slate-600">Estándares ISO</p>
              </div>
            </div>
            <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-5 pl-11">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700 leading-relaxed">
                  <strong>Nota importante:</strong> Algunos documentos muy específicos (como testamentos 
                  o actos de familia en algunos países) pueden requerir firma manuscrita. Te recomendamos 
                  verificar con un abogado si tu documento tiene requisitos especiales.
                </p>
              </div>
            </div>
          </section>

          {/* SECCIÓN 7 */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                <Mail className="w-4 h-4 text-blue-600" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">7. ¿Tienes preguntas?</h2>
            </div>
            <p className="text-slate-600 leading-relaxed mb-6 pl-11">
              Si algo no te queda claro, no dudes en contactarnos.
            </p>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 md:p-8 pl-11">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white border border-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">Correo electrónico:</p>
                    <a href="mailto:legal@visioner7.pe" className="text-blue-600 hover:underline text-sm">legal@visioner7.pe</a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white border border-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">Ubicación:</p>
                    <p className="text-slate-600 text-sm">Lima, Perú</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white border border-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">Tiempo de respuesta:</p>
                    <p className="text-slate-600 text-sm">5-7 días hábiles</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-slate-200 text-center">
          <p className="text-slate-600">
            Gracias por tomarte el tiempo de leer esto. Tu confianza es lo más importante para nosotros.
          </p>
          <p className="text-sm text-slate-500 mt-2">
            El equipo de Inkless
          </p>
        </div>
      </div>
    </div>
  );
}